import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Calendar, CheckCircle2, Clock, MapPin, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicBooking() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_email: "", vehicle_info: "", service_type: "calibration", notes: "" });
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { axios.get(`${BACKEND_URL}/api/public/booking/${token}/availability`).then((r) => setData(r.data)).catch(() => toast.error("Invalid link")); }, [token]);

  const book = async () => {
    if (!slot || !form.customer_name || !form.vehicle_info) return toast.error("Missing fields");
    setLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/public/booking/${token}`, { ...form, requested_slot: slot });
      setSubmitted(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); } finally { setLoading(false); }
  };

  if (!data) return <div className="min-h-screen bg-[#030712] text-slate-200 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  if (submitted) return (
    <div className="min-h-screen bg-[#030712] text-slate-100 flex items-center justify-center p-6" data-testid="booking-success">
      <div className="max-w-md w-full rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center">
        <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
        <h1 className="font-display text-2xl font-semibold text-emerald-300">Booking Confirmed</h1>
        <div className="text-sm text-slate-400 mt-2">Confirmation code</div>
        <div className="font-mono text-2xl font-bold text-blue-300 mt-1">{submitted.confirmation_code}</div>
        <div className="mt-6 text-xs text-slate-400">We'll see you at the scheduled time.</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#030712] text-slate-100" data-testid="public-booking-page">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div className="text-center py-4">
          <Calendar className="w-12 h-12 mx-auto text-blue-400" />
          <h1 className="font-display text-3xl font-semibold mt-2">{data.shop.name}</h1>
          <p className="text-sm text-slate-400 mt-1">Schedule your calibration · Choose a time below</p>
          {data.location && <div className="flex items-center justify-center gap-1 mt-2 text-xs text-slate-500"><MapPin className="w-3 h-3" />{data.location.address}</div>}
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Available Time Slots</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-96 overflow-y-auto">
            {data.slots.filter((s) => s.available).map((s) => (
              <button
                key={s.slot}
                data-testid={`slot-${s.slot}`}
                onClick={() => setSlot(s.slot)}
                className={`text-left rounded-lg border p-3 text-xs transition-colors ${slot === s.slot ? "border-blue-500/50 bg-blue-500/15 text-blue-200 ring-2 ring-blue-500/20" : "border-slate-800 bg-slate-900/40 hover:border-blue-500/30"}`}
              >
                <Clock className="w-3 h-3 inline mr-1" />{s.label}
              </button>
            ))}
          </div>
        </div>

        {slot && (
          <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-300">Your Details</div>
            <Input data-testid="cust-name" placeholder="Your full name *" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="bg-[#030712] border-slate-800" />
            <Input data-testid="cust-phone" placeholder="Phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="bg-[#030712] border-slate-800" />
            <Input data-testid="cust-email" type="email" placeholder="Email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="bg-[#030712] border-slate-800" />
            <Input data-testid="vehicle-info" placeholder="Vehicle (Year Make Model + VIN) *" value={form.vehicle_info} onChange={(e) => setForm({ ...form, vehicle_info: e.target.value })} className="bg-[#030712] border-slate-800" />
            <select data-testid="service-type" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} className="w-full bg-[#030712] border border-slate-800 rounded-lg p-2 text-sm">
              <option value="calibration">ADAS Calibration</option>
              <option value="scan_only">Diagnostic Scan</option>
              <option value="windshield">Windshield + Calibration</option>
              <option value="consultation">Consultation</option>
            </select>
            <Textarea data-testid="booking-notes" placeholder="Notes / damage description (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#030712] border-slate-800" />
            <Button data-testid="book-btn" disabled={loading} onClick={book} className="w-full bg-blue-600 hover:bg-blue-500 h-12 text-base font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Booking"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
